import classnames from 'classnames/bind'
import { useRef, useState } from 'react'
import { useTranslate } from 'react-polyglot'

import { FullScreenImage } from '@unlikelystudio/react-abstract-components'

import Image from '~/components/Abstracts/Image'
import Video from '~/components/Abstracts/Video'
import { ProductHeaderProps } from '~/components/UI/ProductHeader'
import ProductSlider from '~/components/UI/ProductSlider'

import useBodyScrollLock from '~/hooks/useBodyScrollLock'
import useBreakpoint from '~/hooks/useBreakpoint'

import { PANEL } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type ProductHeaderImagesProps = {
  className?: string
  variantsItems?: ProductHeaderProps['variantsItems']
  items?: ProductHeaderProps['items']
  mediaItems?: ProductHeaderProps['mediaItems']
  isQuickbuy?: boolean
  variantIndex?: number
  productName?: string
}

const IMAGE_SIZES = [{ breakpoint: 'md', ratio: 13 / 24 }, { ratio: 1 }]

function ProductHeaderImages({
  className,
  items,
  mediaItems,
  variantsItems,
  variantIndex,
  productName,
}: ProductHeaderImagesProps) {
  const [fullScreenImageIndex, setFullScreenImageIndex] = useState<number>(-1)
  const isMobile = useBreakpoint('md')
  const t = useTranslate()
  const customSliderRef = useRef()

  const processedItems = [
    variantsItems?.[variantIndex] ?? variantsItems?.[0] ?? null,
    ...(mediaItems ? mediaItems : items),
  ]?.filter((item) => item)

  useBodyScrollLock(fullScreenImageIndex >= 0)

  const renderItems = (items) =>
    items?.map((item, index) => {
      const imagePriority = index === 0 ? true : false
      return (
        <div key={`image_video_${index}`} className={cx(css.item)}>
          <div className={css.ratio}>
            {item?.video?.src ? (
              <Video
                className={css.video}
                {...item?.video}
                autoPlay
                loop
                muted
                playsInline
                objectFit="cover"
              />
            ) : item?.image?.src ? (
              <Image
                className={css.image}
                {...item?.image}
                sizesFromBreakpoints={IMAGE_SIZES}
                layout="fill"
                objectFit="cover"
                asPlaceholder={true}
                alt={productName}
                priority={imagePriority}
                loader={null}
                onClick={() => setFullScreenImageIndex(index)}
              />
            ) : null}
          </div>
        </div>
      )
    })

  const renderFullScreenImage = () =>
    isMobile ? (
      <div className={css.fullScreenImage}>
        <div
          className={css.controls}
          onClick={() => setFullScreenImageIndex(-1)}>
          {t('cta.close')}
        </div>
        <Image
          {...items?.[fullScreenImageIndex]?.image}
          objectFit="cover"
          layout="fill"
        />
      </div>
    ) : (
      <FullScreenImage
        key={`fullScreen-image-${items?.[fullScreenImageIndex]?.image?.src}`}
        classes={{
          FullScreenImage: css.fullScreenImage,
          controls: css.controls,
        }}
        imageHeight={items?.[fullScreenImageIndex]?.image?.height}
        imageWidth={items?.[fullScreenImageIndex]?.image?.width}
        image={
          <Image
            {...items?.[fullScreenImageIndex]?.image}
            objectFit="cover"
            layout="fill"
          />
        }
        closeComponent={t(PANEL.CLOSE)}
        onExitFullscreen={() => {
          setFullScreenImageIndex(-1)
        }}
        xStartPercent={50}
        yStartPercent={50}
      />
    )

  return (
    <div
      className={cx(css.ProductHeaderImages, className)}
      ref={customSliderRef}>
      {fullScreenImageIndex > -1 ? renderFullScreenImage() : null}
      {processedItems?.length > 0 && (
        <div className={cx(css.desktopImages, css.hideOnSmallScreen)}>
          {renderItems(processedItems)}
        </div>
      )}
      {processedItems?.length > 0 && (
        <div className={cx(css.mobileImages, css.hideOnLargeScreen)}>
          <ProductSlider sliderClassName={css.slider}>
            {renderItems(processedItems)}
          </ProductSlider>
        </div>
      )}
    </div>
  )
}

ProductHeaderImages.defaultProps = {}

export default ProductHeaderImages
